import { makeStyles } from '@material-ui/styles'
import React from 'react'
import CustomStackedAreaChart from 'core/components/graphs/CustomStackedAreaChart'
import StackedAreaChartTooltip from 'core/components/graphs/StackedAreaChartTooltip'
import HelpContainer from 'core/components/HelpContainer'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { ErrorMessage } from '../validatedForm/ErrorMessage'
import TimePicklist from './TimePicklist'

interface Props {
  data?: any
  onTimeRangeChange?: (range: any) => void
  currentTimeRange?: any
  tooltipMsg?: string | (() => React.ReactNode)
  errorFetchingData?: boolean
}

const TrendGraph = ({
  data,
  onTimeRangeChange,
  currentTimeRange,
  tooltipMsg = '',
  errorFetchingData = false,
}: Props) => {
  const classes = useStyles()

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerRightContent}>
          <Text variant="subtitle2">{data?.title}</Text>
          {tooltipMsg && <HelpContainer title={tooltipMsg}></HelpContainer>}
        </div>
        <div className={classes.seeAll}>
          <TimePicklist
            onChange={onTimeRangeChange}
            currentTimeRange={currentTimeRange}
            isModalView={false}
          />
          <Text variant="subtitle2">
            {data?.averageValue}&nbsp;
            <span className={classes.displayUnit}>{data?.graphUnit || data?.unit}</span>
            &nbsp;
          </Text>
          <Text variant="caption3">{data?.averageText || 'avg.'}</Text>
        </div>
      </div>

      {errorFetchingData ? (
        <div className={classes.errorContainer}>
          <ErrorMessage>
            <Text variant="body2" style={{ color: 'inherit' }}>
              Failed to retrieve usage data from the host
            </Text>
          </ErrorMessage>
        </div>
      ) : (
        <>
          <div className={classes.content}>
            <CustomStackedAreaChart
              values={data?.graph?.values}
              keys={data?.graph?.keys}
              xAxis="name"
              responsive={true}
              verticalAxisLines={!data?.graph?.values?.length}
              horizontalAxisLines={!data?.graph?.values?.length}
              responsiveHeight={120}
              showXAxisTick={true}
              dy={6}
              dx={0}
              customTooltip={
                <StackedAreaChartTooltip
                  keys={data?.graph?.keys}
                  yAxisPostfixLabel={data?.graphUnit || data?.unit}
                />
              }
              xAxisTickFontSize={10}
              yAxisTickFontSize={10}
              axisLine={false}
              type="linear"
            />
          </div>
          <div className={classes.cardsRow}>
            {data?.footerValues?.map((item, index) => (
              <div key={index} className={classes.gridContainer}>
                <Text variant="caption3" className={classes.headerText}>
                  {item?.key}
                </Text>
                <Text variant="caption1">
                  {`${item?.value} ${data?.unit}`}
                  {item?.percent && ` ${item.percent}`}
                </Text>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default TrendGraph

const useStyles = makeStyles((theme: Theme) => ({
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2, 0),
  },
  container: {
    maxWidth: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerRightContent: {
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  displayUnit: {
    fontSize: '12px',
    fontWeight: 400,
  },
  seeAll: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 0, // Ensures no space between "See All" and the icon
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(2),
  },
  gridContainer: {
    display: 'grid',
    marginTop: theme.spacing(2),
    gap: theme.spacing(0.5),
  },
  headerText: {
    color: theme.components.card.passiveText,
  },
  content: {
    paddingTop: theme.spacing(2),
  },
}))
